import React, { useState, useEffect, useRef } from 'react';
import ServiceCard from './servicecard';
import useTheme from '../../ColorConfig/usetheme';

const MobileServicesStack = ({ services, onServiceClick }) => {
  const colors = useTheme();
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isSectionInView, setIsSectionInView] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const containerRef = useRef(null);
  const cardsRef = useRef([]);
  const observerRef = useRef(null);
  const touchStartX = useRef(0);
  const scrollLocked = useRef(false);

  // Intersection Observer to detect when section is in view
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        const isNowInView = entry.isIntersecting;
        setIsSectionInView(isNowInView);
        
        // Enable/disable scroll lock based on section visibility
        scrollLocked.current = isNowInView && 
          currentCardIndex > 0 && 
          currentCardIndex < services.length - 1;
      },
      { threshold: 0.3 }
    );

    if (containerRef.current) {
      observerRef.current.observe(containerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Update scroll lock when current card changes
  useEffect(() => {
    if (isSectionInView) {
      scrollLocked.current = currentCardIndex > 0 && currentCardIndex < services.length - 1;
    }
  }, [currentCardIndex, isSectionInView, services.length]);

  // Prevent vertical scroll only on the container when scroll is locked
  useEffect(() => {
    const handleTouchMove = (e) => {
      if (scrollLocked.current && isSectionInView) {
        // Only prevent vertical scrolling
        const touch = e.touches[0];
        const deltaX = Math.abs(touch.clientX - touchStartX.current);
        const deltaY = Math.abs(touch.clientY - touchStartX.current);
        
        if (deltaY > deltaX) {
          e.preventDefault();
        }
      }
    };

    const handleWheel = (e) => {
      if (scrollLocked.current && isSectionInView) {
        // Only prevent vertical scrolling
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          e.preventDefault();
        }
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('touchmove', handleTouchMove, { passive: false });
      container.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (container) {
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, [isSectionInView]);

  // Touch events for HORIZONTAL card navigation
  useEffect(() => {
    const handleTouchStart = (e) => {
      if (!isSectionInView || isTransitioning) return;
      
      if (containerRef.current && containerRef.current.contains(e.target)) {
        touchStartX.current = e.touches[0].clientX;
      }
    };

    const handleTouchEnd = (e) => {
      if (!isSectionInView || isTransitioning) return;
      
      if (containerRef.current && containerRef.current.contains(e.target)) {
        const touchEndX = e.changedTouches[0].clientX;
        const diff = touchStartX.current - touchEndX;
        
        // Right to Left swipe = Next card
        // Left to Right swipe = Previous card
        if (Math.abs(diff) > 50) {
          handleCardNavigation(diff > 0 ? 'next' : 'prev');
        }
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isSectionInView, isTransitioning, currentCardIndex, services.length]);

  // HORIZONTAL Wheel events for card navigation
  useEffect(() => {
    const handleWheel = (e) => {
      if (!isSectionInView || isTransitioning) return;
      
      // Use deltaX for horizontal scrolling
      const horizontalDelta = e.deltaX;
      
      // If there's significant horizontal movement
      if (Math.abs(horizontalDelta) > 10) {
        if (scrollLocked.current) {
          e.preventDefault();
          e.stopPropagation();
        }
        
        // Positive deltaX = scrolling right (next card)
        // Negative deltaX = scrolling left (prev card)
        const direction = horizontalDelta > 0 ? 'next' : 'prev';
        handleCardNavigation(direction);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, [isSectionInView, isTransitioning, currentCardIndex, services.length]);

  // Card navigation handler with smooth looping
  const handleCardNavigation = (direction) => {
    if (isTransitioning) return;

    setIsTransitioning(true);

    if (direction === 'next') {
      // Smooth loop to first card if at last card
      setCurrentCardIndex(prev => (prev + 1) % services.length);
      
      if (navigator.vibrate) {
        navigator.vibrate([15, 10, 15]);
      }
    } else if (direction === 'prev') {
      // Smooth loop to last card if at first card
      setCurrentCardIndex(prev => (prev - 1 + services.length) % services.length);
      
      if (navigator.vibrate) {
        navigator.vibrate([15, 10, 15]);
      }
    }
    
    setTimeout(() => setIsTransitioning(false), 800);
  };

  // Curtain reveal positioning with smooth looping
  useEffect(() => {
    if (services.length === 0 || cardsRef.current.length === 0) return;

    cardsRef.current.forEach((card, index) => {
      if (!card) return;

      const position = index - currentCardIndex;
      let translateX = 0;
      let translateY = 0;
      let opacity = 0;
      let scale = 1;
      let zIndex = 0;
      let clipPath = 'inset(0 0 0 0)';

      // Handle looping positions for smooth transitions
      let adjustedPosition = position;
      
      // If position is too negative (cards before the first), show them as coming from the end
      if (position < -services.length / 2) {
        adjustedPosition = position + services.length;
      }
      // If position is too positive (cards after the last), show them as coming from the beginning
      else if (position > services.length / 2) {
        adjustedPosition = position - services.length;
      }

      if (adjustedPosition < 0) {
        translateY = adjustedPosition * 80;
        translateX = -100;
        opacity = 0;
        scale = 0.8;
        zIndex = 10 + adjustedPosition;
        clipPath = 'inset(0 100% 0 0)';
      } else if (adjustedPosition === 0) {
        translateX = 0;
        translateY = 0;
        opacity = 1;
        scale = 1;
        zIndex = 100;
        clipPath = 'inset(0 0 0 0)';
      } else if (adjustedPosition === 1) {
        translateX = 50;
        translateY = 40;
        opacity = 0.6;
        scale = 0.85;
        zIndex = 90;
        clipPath = 'inset(0 0 0 0)';
      } else if (adjustedPosition === 2) {
        translateX = 70;
        translateY = 40;
        opacity = 0.3;
        scale = 0.75;
        zIndex = 80;
        clipPath = 'inset(0 0 0 0)';
      } else {
        translateX = 100;
        translateY = adjustedPosition * 60;
        opacity = 0;
        scale = 0.7;
        zIndex = 70 - adjustedPosition;
        clipPath = 'inset(0 0 0 100%)';
      }

      card.style.transform = `translateX(${translateX}%) translateY(${translateY}%) scale(${scale})`;
      card.style.opacity = opacity;
      card.style.zIndex = zIndex;
      card.style.clipPath = clipPath;
      card.style.borderRadius = '20px';
      card.style.overflow = 'hidden';
      card.style.transition = 'all 0.7s cubic-bezier(0.65, 0, 0.35, 1)';
    });
  }, [currentCardIndex, services.length]);

  if (services.length === 0) {
    return null;
  }

  const containerStyle = {
    position: 'relative',
    minHeight: '50vh',
    height: '20vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0',
    margin: '0',
    overflow: 'hidden',
    width: '100%',
    boxSizing: 'border-box',
    touchAction: scrollLocked.current ? 'pan-x' : 'pan-y',
    transition: 'all 0.7s cubic-bezier(0.65, 0, 0.35, 1)'
  };

  const stackStyle = {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'visible',
    marginTop: '-420px',
    transition: 'all 0.7s cubic-bezier(0.65, 0, 0.35, 1)'
  };

  const cardWrapperStyle = {
    position: 'absolute',
    width: '90%',
    maxWidth: '320px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)',
    marginBottom: '-300px',
    borderRadius: '20px',
    overflow: 'hidden',
  };

  // HORIZONTAL Indicators style - ALWAYS show both indicators
  const indicatorsStyle = {
    position: 'absolute',
    bottom: '65px',
    left: '30%',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'row',
    gap: '35px',
    zIndex: 1000,
    pointerEvents: 'none',
  };

  const pulseIndicatorStyle = {
    width: '52px',
    height: '52px',
    background: 'linear-gradient(135deg, rgba(229, 118, 39, 0.95), rgba(255, 140, 66, 0.95))',
    backdropFilter: 'blur(15px)',
    borderRadius: '26px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    boxShadow: '0 8px 32px rgba(229, 118, 39, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    pointerEvents: 'all',
    transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
    position: 'relative',
    overflow: 'hidden'
  };

  const pulseAnimationStyle = {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.4), transparent)',
    borderRadius: '26px',
    animation: 'pulseGlow 2s ease-in-out infinite',
    pointerEvents: 'none'
  };

  const iconStyle = {
    width: '24px',
    height: '24px',
    color: 'white',
    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
    transition: 'all 0.3s ease'
  };

  const hoverEffect = {
    transform: 'scale(1.15)',
    boxShadow: '0 12px 40px rgba(229, 118, 39, 0.5)',
    background: 'linear-gradient(135deg, rgba(229, 118, 39, 1), rgba(255, 140, 66, 1))'
  };

  const activeEffect = {
    transform: 'scale(0.95)',
    boxShadow: '0 4px 20px rgba(229, 118, 39, 0.4)'
  };

  // HORIZONTAL Arrow icons
  const LeftChevron = () => (
    <svg style={iconStyle} viewBox="0 0 24 24" fill="none">
      <path 
        d="M15 7L9 12L15 17" 
        stroke="currentColor" 
        strokeWidth="2.5" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={{ animation: 'bounceLeft 1.5s ease-in-out infinite' }}
      />
      <path 
        d="M9 12H21" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeDasharray="4 4"
        opacity="0.7"
      />
    </svg>
  );

  const RightChevron = () => (
    <svg style={iconStyle} viewBox="0 0 24 24" fill="none">
      <path 
        d="M9 7L15 12L9 17" 
        stroke="currentColor" 
        strokeWidth="2.5" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={{ animation: 'bounceRight 1.5s ease-in-out infinite' }}
      />
      <path 
        d="M15 12H3" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeDasharray="4 4"
        opacity="0.7"
      />
    </svg>
  );

  return (
    <div ref={containerRef} style={containerStyle}>
      <style>
        {`
          @keyframes pulseGlow {
            0%, 100% { opacity: 0.6; transform: scale(1); }
            50% { opacity: 0.9; transform: scale(1.05); }
          }
          
          @keyframes bounceLeft {
            0%, 20%, 50%, 80%, 100% { transform: translateX(0); }
            40% { transform: translateX(-3px); }
            60% { transform: translateX(-1px); }
          }
          
          @keyframes bounceRight {
            0%, 20%, 50%, 80%, 100% { transform: translateX(0); }
            40% { transform: translateX(3px); }
            60% { transform: translateX(1px); }
          }
          
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-5px); }
          }
        `}
      </style>

      <div style={stackStyle}>
        {services.map((service, index) => (
          <div
            key={service.id}
            ref={el => {
              cardsRef.current[index] = el;
            }}
            style={cardWrapperStyle}
          >
            <ServiceCard 
              service={service} 
              onServiceClick={onServiceClick}
              isMobile={true}
              index={index}
              isActive={index === currentCardIndex}
            />
          </div>
        ))}
      </div>
      
      {/* HORIZONTAL Navigation indicators - ALWAYS BOTH */}
      <div style={indicatorsStyle}>
        {/* Left Indicator - Previous card - ALWAYS VISIBLE */}
        <button 
          style={{
            ...pulseIndicatorStyle,
            animation: 'float 3s ease-in-out infinite'
          }}
          onMouseEnter={(e) => Object.assign(e.target.style, hoverEffect)}
          onMouseLeave={(e) => Object.assign(e.target.style, pulseIndicatorStyle)}
          onMouseDown={(e) => Object.assign(e.target.style, activeEffect)}
          onMouseUp={(e) => Object.assign(e.target.style, hoverEffect)}
          onClick={() => handleCardNavigation('prev')}
          disabled={isTransitioning}
        >
          <div style={pulseAnimationStyle} />
          <LeftChevron />
        </button>
        
        {/* Right Indicator - Next card - ALWAYS VISIBLE */}
        <button 
          style={{
            ...pulseIndicatorStyle,
            animation: 'float 3s ease-in-out infinite'
          }}
          onMouseEnter={(e) => Object.assign(e.target.style, hoverEffect)}
          onMouseLeave={(e) => Object.assign(e.target.style, pulseIndicatorStyle)}
          onMouseDown={(e) => Object.assign(e.target.style, activeEffect)}
          onMouseUp={(e) => Object.assign(e.target.style, hoverEffect)}
          onClick={() => handleCardNavigation('next')}
          disabled={isTransitioning}
        >
          <div style={pulseAnimationStyle} />
          <RightChevron />
        </button>
      </div>
    </div>
  );
};

export default MobileServicesStack;